import { ChangeDetectionStrategy, Component, HostListener, OnInit, ViewChild, inject } from '@angular/core';
import { Context } from '@service/context.service';
import { AbstractContentComponent } from '../abstract-content.component';
import { SelectItem, TreeNode } from 'primeng/api';
import { Column } from '@shared/table/column';
import { TreeTableComponent } from '@shared/table/tree-table.component';
import { SaveableDataComponent } from '@shared/saveable-data-component';
import { CustomersTreeTableService } from './customers-tree-table.service';
import { TABLE_SERVICE } from '@shared/table/table-service';
import { AnwenderbetriebService } from '@service/anwenderbetrieb.service';
import { HeaderGroup } from '@shared/table/abstract-table.component';
import { today } from '../util';

@Component({
  selector: 'app-customers',
  templateUrl: './customers.component.html',
  styleUrls: ['./customers.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: TABLE_SERVICE,
      useClass: CustomersTreeTableService,
    },
  ],
  imports: [TreeTableComponent],
})
export class CustomersComponent extends AbstractContentComponent implements OnInit, SaveableDataComponent {
  private treeTableService = inject<CustomersTreeTableService>(TABLE_SERVICE);
  private anwenderbetriebService = inject(AnwenderbetriebService);

  tree: TreeNode[] = [];
  columns: Column[] = [];
  headerGroupColumns: HeaderGroup[][] = [];
  focusDate: Date | undefined = today();

  @ViewChild('treeTable') private treeTableComponent!: TreeTableComponent;

  constructor() {
    super(Context.Mandant, 'MENU.CUSTOMERS');
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: { returnValue: boolean }): void {
    if (this.hasUnsavedData()) {
      $event.returnValue = true;
    }
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setColumns();
    this.setHeaderGroupColumns();
  }

  isTableEditable(): boolean {
    return this.isRodixAdmin();
  }

  hasUnsavedData(): boolean {
    return this.treeTableComponent?.hasChanges();
  }

  focusDateChange(focusDate?: Date): void {
    this.focusDate = focusDate;
    this.treeTableService.reload();
  }

  protected onLanguageChange(): void {
    this.setColumns();
    this.setHeaderGroupColumns();
    this.markForCheck();
  }

  protected onMandantIdChange(): void {
    this.setColumns();
    this.setHeaderGroupColumns();
  }

  private setColumns(): void {
    const anwenderbetriebId = this.getAnwenderbetriebId();
    if (!anwenderbetriebId) return;
    this.anwenderbetriebService.getOrganisationalUnits(anwenderbetriebId).subscribe((organisationalUnits) => {
      this.columns = [
        {
          field: 'customerId',
          headerKey: 'CUSTOMERS.CUSTOMER_ID',
          width: '180px',
          level: 0,
          editable: this.isTableEditable(),
          onlyInitialModificationAllowed: true,
          required: true,
        },
        {
          field: 'firstName',
          headerKey: 'GLOBAL.FIRST_NAME',
          minWidth: '250px',
          level: 0,
          editable: this.isTableEditable(),
        },
        {
          field: 'lastName',
          headerKey: 'GLOBAL.LAST_NAME',
          minWidth: '250px',
          level: 0,
          editable: this.isTableEditable(),
        },
        {
          field: 'dateOfBirth',
          headerKey: 'GLOBAL.DATE_OF_BIRTH',
          width: '180px',
          filterType: 'date',
          level: 0,
          editable: this.isTableEditable(),
        },
        {
          field: 'gender',
          headerKey: 'GLOBAL.GENDER',
          width: '180px',
          level: 0,
          editable: this.isTableEditable(),
          options: [
            { label: this.translate('GLOBAL.GENDER_TYPE.F'), value: 'F' },
            { label: this.translate('GLOBAL.GENDER_TYPE.M'), value: 'M' },
            { label: this.translate('GLOBAL.GENDER_TYPE.I'), value: 'I' },
            { label: this.translate('GLOBAL.GENDER_TYPE.U'), value: 'U' },
          ],
        },
        {
          field: 'caseId',
          headerKey: 'CUSTOMERS.CASE_ID',
          width: '180px',
          level: 1,
          editable: this.isTableEditable(),
          onlyInitialModificationAllowed: true,
          required: true,
        },
        {
          field: 'entryDate',
          headerKey: 'CUSTOMERS.ENTRY_DATE',
          width: '180px',
          filterType: 'date',
          level: 1,
          editable: this.isTableEditable(),
          required: true,
        },
        {
          field: 'leaveDate',
          headerKey: 'CUSTOMERS.LEAVE_DATE',
          width: '180px',
          filterType: 'date',
          level: 1,
          editable: this.isTableEditable(),
          required: true,
        },
        {
          field: 'validFrom',
          headerKey: 'GLOBAL.GUELTIG_AB',
          width: '180px',
          filterType: 'date',
          level: 2,
          editable: this.isTableEditable(),
          required: true,
        },
        {
          field: 'validTo',
          headerKey: 'GLOBAL.GUELTIG_BIS',
          width: '180px',
          filterType: 'date',
          level: 2,
          editable: this.isTableEditable(),
        },
        {
          field: 'organisationalUnitId',
          headerKey: 'CUSTOMERS.ORGANISATIONAL_UNIT_ID',
          width: '400px',
          level: 2,
          editable: this.isTableEditable(),
          options: organisationalUnits
            .sort((a, b) => (a.organisationId < b.organisationId ? -1 : 1))
            .map(
              (o) =>
                ({
                  value: o.organisationId,
                  label: `${o.organisationId} - ${o.bezeichnung}`,
                }) as SelectItem
            ),
        },
        {
          field: 'payScaleLevel',
          headerKey: 'CUSTOMERS.PAY_SCALE_LEVEL',
          minWidth: '250px',
          level: 2,
          editable: this.isTableEditable(),
        },
        {
          field: 'insuranceCategory',
          headerKey: 'CUSTOMERS.INSURANCE_CATEGORY',
          minWidth: '250px',
          level: 2,
          editable: this.isTableEditable(),
          options: [
            { label: this.translate('CUSTOMERS.INSURANCE_CATEGORY_TYPE.allgemein'), value: 'allgemein' },
            { label: this.translate('CUSTOMERS.INSURANCE_CATEGORY_TYPE.halbprivat'), value: 'halbprivat' },
            { label: this.translate('CUSTOMERS.INSURANCE_CATEGORY_TYPE.privat'), value: 'privat' },
            { label: this.translate('CUSTOMERS.INSURANCE_CATEGORY_TYPE.unbekannt'), value: 'unbekannt' },
          ],
        },
        {
          field: 'discipline',
          headerKey: 'CUSTOMERS.DISCIPLINE',
          minWidth: '250px',
          level: 2,
          editable: this.isTableEditable(),
        },
        {
          field: 'typeOfStay',
          headerKey: 'CUSTOMERS.TYPE_OF_STAY',
          minWidth: '250px',
          level: 2,
          editable: this.isTableEditable(),
          options: [
            { label: this.translate('CUSTOMERS.TYPE_OF_STAY_TYPE.ambulant'), value: 'ambulant' },
            { label: this.translate('CUSTOMERS.TYPE_OF_STAY_TYPE.stationär'), value: 'stationär' },
            { label: this.translate('CUSTOMERS.TYPE_OF_STAY_TYPE.teilstationär'), value: 'teilstationär' },
            { label: this.translate('CUSTOMERS.TYPE_OF_STAY_TYPE.unbekannt'), value: 'unbekannt' },
          ],
        },
      ];
      this.markForCheck();
    });
  }

  private setHeaderGroupColumns(): void {
    this.headerGroupColumns = [
      [
        { title: this.translate('CUSTOMERS.CUSTOMER'), colSpan: 5 },
        { title: this.translate('CUSTOMERS.CUSTOMER_CASE'), colSpan: 3 },
        { title: this.translate('CUSTOMERS.CUSTOMER_CASE_MUTATION'), colSpan: 7 },
      ],
    ];
  }
}
